import { useTranslation } from '../../../hooks/useTranslation'
import type { ProjectDetailResponse } from '../../../types'

interface ScoringTabProps {
  data: ProjectDetailResponse
}

function formatDate(isoString: string | null): string {
  if (!isoString) return '—'
  const date = new Date(isoString)
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function ScoringTab({ data }: ScoringTabProps) {
  const { t } = useTranslation()
  const scores = data.scores
  const composite = data.project.score_composite
  const scoredCount = scores.filter(s => s.score !== null).length
  const totalCount = scores.length

  const categoryScores = new Map<string, Array<{ name: string; score: number | null }>>()
  scores.forEach(s => {
    if (!categoryScores.has(s.dimension.category)) {
      categoryScores.set(s.dimension.category, [])
    }
    categoryScores.get(s.dimension.category)!.push({
      name: s.dimension.name,
      score: s.score,
    })
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-8">
        <div className="flex-1">
          <div className="text-caption font-semibold text-text-secondary uppercase tracking-wide mb-2">
            {t('detail.composite_score')}
          </div>
          <div className="text-h2 font-bold text-text-primary">
            {composite !== null ? (composite * 100).toFixed(0) : '—'}
            {composite !== null && <span className="text-body text-text-secondary">/100</span>}
          </div>
        </div>

        <div className="flex-1">
          <div className="text-caption font-semibold text-text-secondary uppercase tracking-wide mb-2">
            {t('detail.scoring_confidence')}
          </div>
          <div className="text-h3 font-semibold text-text-primary mb-1">
            {scoredCount}/{totalCount}
          </div>
          <div className="w-full bg-bg-input rounded-full h-2 overflow-hidden">
            <div
              className="bg-success h-full transition-all"
              style={{ width: `${totalCount > 0 ? (scoredCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {Array.from(categoryScores.entries()).map(([category, dims]) => (
          <div key={category} className="bg-bg-input rounded-lg p-4 border border-border-subtle">
            <div className="text-caption font-semibold text-text-secondary uppercase tracking-wide mb-3">
              {category}
            </div>
            <div className="space-y-3">
              {dims.map((dim, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-body text-text-primary">{dim.name}</span>
                    {dim.score !== null && (
                      <span className="text-body font-semibold text-text-primary">{dim.score}/3</span>
                    )}
                  </div>
                  {dim.score === null && (
                    <div className="text-body-sm text-text-tertiary">Unscored</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3 border-t border-border-subtle pt-6">
        <div className="text-caption font-semibold text-text-secondary uppercase tracking-wide">
          {t('detail.all_scores')}
        </div>
        {scores.map((s, idx) => (
          <div key={idx} className="bg-bg-input rounded p-3 border border-border-subtle">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <div className="text-body font-semibold text-text-primary">{s.dimension.name}</div>
                <div className="text-body-sm text-text-tertiary">{s.dimension.category}</div>
              </div>
              {s.score !== null ? (
                <div className="text-body-sm font-semibold text-success bg-success/10 px-2 py-1 rounded">
                  {s.score}/3
                </div>
              ) : (
                <div className="text-body-sm text-text-tertiary">—</div>
              )}
            </div>
            {s.dimension.description && (
              <div className="text-body-sm text-text-tertiary mb-2">{s.dimension.description}</div>
            )}
            {s.justification && (
              <div className="text-body-sm text-text-secondary bg-bg-surface-hover rounded p-2">
                {s.justification}
              </div>
            )}
            {s.scored_by && s.scored_at && (
              <div className="text-caption text-text-tertiary mt-2">
                {t('detail.scored_by')} {s.scored_by.name} ({formatDate(s.scored_at)})
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
